import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import CheckoutSteps from '../components/CheckoutSteps';
import { createOrder } from '../actions/orderActions';
import { ORDER_CREATE_RESET } from '../constants/orderConstants';

const PlaceOrderScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const cart = useSelector((state) => state.cart);
  
  // Calculate prices
  const addDecimals = (num) => {
    return (Math.round(num * 100) / 100).toFixed(2);
  };
  
  cart.itemsPrice = addDecimals(
    cart.cartItems.reduce((acc, item) => acc + item.price * item.qty, 0)
  );
  
  // Shipping is free for orders above 10,000 KES, otherwise 1500 KES
  cart.shippingPrice = addDecimals(cart.itemsPrice > 10000 ? 0 : 1500);
  
  // 16% VAT
  cart.taxPrice = addDecimals(Number((0.16 * cart.itemsPrice).toFixed(2)));
  
  cart.totalPrice = (
    Number(cart.itemsPrice) +
    Number(cart.shippingPrice) +
    Number(cart.taxPrice)
  ).toFixed(2);
  
  const orderCreate = useSelector((state) => state.orderCreate);
  const { order, success, error } = orderCreate;
  
  useEffect(() => {
    if (success) {
      navigate(`/order/${order._id}`);
      dispatch({ type: ORDER_CREATE_RESET });
    }
    // eslint-disable-next-line
  }, [navigate, success]);
  
  const placeOrderHandler = () => {
    dispatch(
      createOrder({
        orderItems: cart.cartItems,
        shippingAddress: cart.shippingAddress,
        paymentMethod: cart.paymentMethod,
        itemsPrice: cart.itemsPrice,
        shippingPrice: cart.shippingPrice,
        taxPrice: cart.taxPrice,
        totalPrice: cart.totalPrice,
      })
    );
  };

  return (
    <>
      <CheckoutSteps step1 step2 step3 step4 />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Shipping</h2>
            <p className="mb-1">
              <strong>Address:</strong> {cart.shippingAddress.address},{' '}
              {cart.shippingAddress.city} {cart.shippingAddress.postalCode},{' '}
              {cart.shippingAddress.country}
            </p>
            {cart.shippingAddress.deliveryInstructions && (
              <p className="mb-0">
                <strong>Instructions:</strong> {cart.shippingAddress.deliveryInstructions}
              </p>
            )}
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Payment Method</h2>
            <p className="mb-0">
              <strong>Method:</strong> {cart.paymentMethod}
            </p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Order Items</h2>
            {cart.cartItems.length === 0 ? (
              <Message>Your cart is empty</Message>
            ) : (
              <div className="divide-y divide-gray-200">
                {cart.cartItems.map((item, index) => (
                  <div key={index} className="py-3 flex items-center">
                    <div className="w-16 h-16 flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>
                    <div className="ml-4 flex-1">
                      <Link
                        to={`/product/${item.product}`}
                        className="text-primary hover:underline"
                      >
                        {item.name}
                      </Link>
                    </div>
                    <div className="text-right">
                      {item.qty} x KES {item.price.toLocaleString()} = KES{' '}
                      {(item.qty * item.price).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
            <h2 className="text-xl font-semibold mb-4 pb-3 border-b">Order Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <div>Items</div>
                <div>KES {Number(cart.itemsPrice).toLocaleString()}</div>
              </div>
              
              <div className="flex justify-between">
                <div>Shipping</div>
                <div>KES {Number(cart.shippingPrice).toLocaleString()}</div>
              </div>
              
              <div className="flex justify-between">
                <div>Tax (16% VAT)</div>
                <div>KES {Number(cart.taxPrice).toLocaleString()}</div>
              </div>
              
              <div className="flex justify-between font-bold text-lg pt-3 border-t">
                <div>Total</div>
                <div>KES {Number(cart.totalPrice).toLocaleString()}</div>
              </div>
              
              {error && <Message variant="danger">{error}</Message>}
              
              <button
                type="button"
                className={`w-full py-3 mt-4 ${
                  cart.cartItems.length === 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark'
                } text-white font-medium rounded-md`}
                disabled={cart.cartItems.length === 0}
                onClick={placeOrderHandler}
              >
                Place Order
              </button>
            </div>
            
            <div className="mt-6 text-sm text-gray-600">
              <p className="mb-2">
                By placing your order, you agree to IKappal's{' '}
                <Link to="/terms" className="text-primary hover:underline">
                  terms and conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary hover:underline">
                  privacy policy
                </Link>
                .
              </p>
              <p className="mb-0">
                For questions about bulk pricing or custom orders,{' '}
                <Link to="/contact" className="text-primary hover:underline">
                  contact our sales team
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlaceOrderScreen;
